import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import { formatLotNumber } from '../../versa/misc.js';
import filter, { resolveFilterLabelsToIds, translate } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany } from '../../versa/misc.js';
import pdf from '../../versa/pdf.js';
import view from '../views/stocks.js';
import form from '../forms/stocks.js';
import move_stock_form from '../forms/stocks-move-stock.js';
import select_printer_form from '../forms/stocks-select-printer.js';
import report_modal_form from '../forms/stocks-report-modal.js';
import export_csv_form from '../forms/stocks-export-csv.js';
import * as types from '../types.js';

function downloadCsv(base64Content, filename) {
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: 'text/csv;charset=utf-8' });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'relatorio.csv';
	a.click();
	URL.revokeObjectURL(a.href);
}

const stocks = function()
{

	let stocks_crud = crud.create({
		default_columns: '1,2,12,13,15,16,17,18,19,20,21,22,24,26,25,28,27,29,30,3,4,5,6,7,8,9,10',
		insertRecord() {
			// Tela de estoque é somente consulta; criação/alteração é feita por entradas, movimentação e inventário
		},
		async run(module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options = {}) {
			// Grade de estoques: índices SQL mudaram (coluna Fabricante). Migra setup salvo uma vez.
			try {
				const raw = localStorage.getItem('stocks.setup');
				if (raw) {
					const s = JSON.parse(raw);
					if (!s.stocks_grid_v2) {
						s.columns = this.default_columns;
						s.stocks_grid_v2 = true;
						localStorage.setItem('stocks.setup', JSON.stringify(s));
					}
				}
			} catch (_) { /* ignore */ }
			await Object.getPrototypeOf(this).run.call(this, module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, { ...options, skipInitialLoad: true });
			// Ocultar Inserir (tela somente consulta)
			const insertLi = this.target_node?.querySelector?.('li[hash="#insert"]');
			if (insertLi) insertLi.style.display = 'none';
			// Renderizar grade vazia com mensagem até o usuário filtrar
			const grid_node = this.target_node?.querySelector?.('#grid');
			if (grid_node) {
				grid_node.innerHTML = '<p class="mtm" style="color:var(--color-muted)">Clique em <strong>Filtrar</strong> e depois <strong>Aplicar</strong> para carregar os dados (ou aplicar filtros para refinar a busca).</p>';
			}
		},
		async loadBaseData()
		{
			const q = this.queryAll();
			await Promise.all([
				api.call(api.list_companies, q).then((data) => db.set('companies', data)).catch(error => notify(error)),
				api.call(api.list_warehouses, q).then((data) => db.set('warehouses', data)).catch(error => notify(error)),
				api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data)).catch(error => notify(error)),
				api.call(api.list_products, q).then((data) => db.set('products', data)).catch(error => notify(error)),
				api.call(api.list_lots, q).then((data) => db.set('lots', data)).catch(error => notify(error)),
				api.call(api.list_programs, q).then((data) => db.set('programs', data)).catch(error => notify(error)),
				api.call(api.list_devices, q).then((data) => db.set('devices', data)).catch(error => notify(error))
			]);
		},
		async queryData(query)
		{
			return await api.call(api.list_stocks, query);
		},
		async loadRecord(id)
		{
			const data = await api.call(api.select_stock, ...JSON.parse(id));
			if (data && typeof data === 'object') {
				// Mapear dados enriquecidos para os campos somente leitura do modal
				data.stock_sku = data.sku ?? '';
				data.stock_product_name = data.product_name ?? '';
				data.stock_ncm = data.ncm ?? '';
				data.stock_gtin = data.gtin ?? '';
				data.stock_packaging_code = data.packaging_code ?? '';
				data.stock_packaging_factor = data.packaging_factor ?? '';
				data.stock_packaging_qr = data.packaging_qr_code ?? '';
				data.stock_lot_number = formatLotNumber(data.lot_number) ?? '';
				data.stock_expiration_date = data.expiration_date ?? '';
				data.stock_manufacture_date = data.manufacture_date ?? '';
				data.stock_program_name = data.program_name ?? '';
				data.stock_codigo_cliente = data.codigo_cliente ?? '';
				data.stock_input_created_on = data.input_created_on ? (() => { const d = new Date(data.input_created_on); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`; })() : '';
				data.stock_input_id = data.input_id ?? '';
				data.stock_invoice_number = data.invoice_number ?? '';
				data.stock_supplier_name = data.supplier_name ?? '';
				data.stock_manufacturer_nick = data.manufacturer_nick ?? '';
			}
			return data;
		},
		async saveRecord() {
			// Tela de estoque é somente consulta; não persiste alterações por aqui
			return false;
		},
		async editFilter() {
			// MT-596E375: recarregar locations antes de abrir filtro para escopar por empresa/armazém
			api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data)).catch(() => {});
			filter.edit(this.applyFilter.bind(this), this.filter, {
				reloadLocations: async (formData) => {
					const resolved = await resolveFilterLabelsToIds(formData, this.fieldspecs);
					const parts = [];
					const top = 10;
					for (let i = 0; i < top; i++) {
						const f = resolved[`field-${i}`];
						const v = resolved[`value-${i}`];
						if (f === 'company_id' && v) parts.push(`company_id*eq*${v}`);
						else if (f === 'warehouse_id' && v != null && v !== '') parts.push(`warehouse_id*eq*${v}`);
					}
					if (parts.length === 0) return;
					const params = new URLSearchParams();
					params.append('filter', parts.join(','));
					params.append('sort', '4');
					params.append('limit', '0');
					const data = await api.call(api.list_locations, '?' + params.toString());
					if (data && Array.isArray(data)) db.set('locations', data);
				}
			});
		},
		async applyFilter(data) {
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data)).catch(() => {});
			this.loadPage(1);
		},
		moveStock()
		{
			const showResult = async (success) =>
			{
				if (success)
				{
					this.loadPage();	// Show updated stocks
					fs.destroy();
				}
			}
			const sendRequest = async (data) =>
			{
				let params = {
					'source_company_id': data.source_warehouse_company_id,
					'source_warehouse_id': Number(data.source_warehouse_id),
					'source_location_id': Number(data.source_location_id),
					'source_stock': data.source_stock,
					'target_company_id': data.target_warehouse_company_id,
					'target_warehouse_id': Number(data.target_warehouse_id),
					'target_location_id': Number(data.target_location_id),
					'target_stock': data.target_stock,
					'quantity': Number(data.quantity),
					'reason': data.reason,
					'output_id': Number(data.output_id)
				}
				let resp = await api.call(api.move_stock, data.lot_company_id, data.lot_id, params).then(showResult);
			}
			let fs = formset.create(move_stock_form, sendRequest);
			const crud = this;
			const origInit = fs.init.bind(fs);
			fs.init = async function()
			{
				origInit();
				// Preencher com estoque selecionado na grid
				const selected = await crud.getSelectedIds(['company_id', 'id']);
				if (selected?.length === 1)
				{
					const [company_id, stock_id] = selected[0];
					const stock = await api.call(api.select_stock, company_id, stock_id);
					if (stock)
					{
						fs.form.setValues({
							stock_sku: stock.sku ?? '',
							lot_company_id: stock.lot_company_id,
							lot_id: stock.lot_id,
							quantity: stock.quantity_available ?? 1,
							source_warehouse_company_id: stock.company_id,
							source_warehouse_id: stock.warehouse_id,
							source_location_id: stock.location_id
						});
						fs.form.refresh();
					}
				}
			};
			fs.init();
		},
		reportPayload(filterData, extra = {})
		{
			const filterExpr = filterData ? translate(filterData) : '';
			const sortVal = this.sort_column * (this.sort_order ? -1 : 1);
			return { filter: filterExpr || undefined, sort: sortVal, ...extra };
		},
		openReportModal(title, apiMethod)
		{
			let reportFilter = null;
			const crud = this;
			const descriptor = { ...report_modal_form };
			descriptor.html = descriptor.html.replace('<div class="title" id="report-title">Relatório</div>', `<div class="title" id="report-title">${title}</div>`);
			const fs = formset.create(descriptor, async (data) =>
			{
				fs.destroy();
				const payload = crud.reportPayload(reportFilter, { format: data.format });
				try {
					const json = await api.call(apiMethod, payload);
					if (json?.pdf) pdf(json.pdf);
					else if (json?.csv) {
						downloadCsv(json.csv, json.filename || 'relatorio.csv');
						notify('Download iniciado.', 'Sucesso');
					}
					else notify('Nenhum dado para o relatório.', 'Aviso');
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init = (() =>
			{
				Object.getPrototypeOf(fs).init.call(fs);
				const node = fs.dialog?.getNode?.();
				if (node) {
					const btnFilters = node.querySelector('#btn-filters');
					const summary = node.querySelector('#filter-summary');
					const updateSummary = () => {
						summary.textContent = reportFilter ? 'Filtros definidos' : 'Sem filtros (todos os registros)';
					};
					btnFilters?.addEventListener('click', async () =>
					{
						api.call(api.list_locations, await crud.queryFilteredForLocations()).then((data) => db.set('locations', data)).catch(() => {});
						filter.edit(async (data) =>
						{
							reportFilter = await resolveFilterLabelsToIds(data, crud.fieldspecs);
							api.call(api.list_locations, await crud.queryFilteredForLocations()).then((d) => db.set('locations', d)).catch(() => {});
							updateSummary();
						}, reportFilter, { reloadLocations: async (formData) =>
						{
							const resolved = await resolveFilterLabelsToIds(formData, crud.fieldspecs);
							const parts = [];
							for (let i = 0; i < 10; i++) {
								const f = resolved[`field-${i}`];
								const v = resolved[`value-${i}`];
								if (f === 'company_id' && v) parts.push(`company_id*eq*${v}`);
								else if (f === 'warehouse_id' && v != null && v !== '') parts.push(`warehouse_id*eq*${v}`);
							}
							if (parts.length === 0) return;
							const params = new URLSearchParams();
							params.append('filter', parts.join(','));
							params.append('sort', '4');
							params.append('limit', '0');
							const data = await api.call(api.list_locations, '?' + params.toString());
							if (data && Array.isArray(data)) db.set('locations', data);
						}});
					});
					updateSummary();
				}
			})();
		},
		printBySKU() { this.openReportModal('Estoque Consolidado por SKU', api.print_stocks_by_sku); },
		printByLot() { this.openReportModal('Estoque Consolidado por Lote', api.print_stocks_by_lot); },
		printByLocation() { this.openReportModal('Estoque Consolidado por Posição', api.print_stocks_by_location); },
		exportCsv()
		{
			const fs = formset.create(export_csv_form, async (data) =>
			{
				fs.destroy();
				const payload = {
					company_id: data.company_id,
					warehouse_id: data.warehouse_id ? Number(data.warehouse_id) : null,
					program_id: data.program_id ? Number(data.program_id) : null
				};
				try {
					const json = await api.call(api.export_stocks_csv, payload);
					if (json?.csv) {
						downloadCsv(json.csv, json.filename || 'estoque.csv');
						notify('Download iniciado.', 'Sucesso');
					} else {
						notify('Nenhum dado para exportar.', 'Aviso');
					}
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init();
		},
		printLotLabels()
		{
			const sendRequest = async (data) =>
			{
				let payload = this.reportPayload({ company_id: data.company_id, device_id: data.device_id });
				if (!payload.filter) {
					let filtered = await this.queryFilteredData();
					payload.ids = splitIdsByCompany(filtered.map(r => [r.company_id, r.id]));
				}
				payload.return_zpl = !!data.use_local_agent;
				const result = await api.call(api.print_lot_labels, payload);
				if (result?.zpl) {
					try {
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					} catch (e) {
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			}
			let fs = formset.create(select_printer_form, sendRequest);
			fs.init = (() =>
			{
				document.querySelector(`${fs.form.selector} #count`).innerHTML = this.record_count;
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
	});
	
	// Ordem = colunas da grade (alinhado a js/views/stocks.js). Índice = posição no SELECT SQL_LIST_BY_CREDENTIAL_ENRICHED (src/repos/stocks.py).
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Estoque', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do estoque' },
		{ index: 12, field: 'sku', label: 'SKU', types: [types.name()], placeholder: '', title: 'SKU do produto' },
		{ index: 13, field: 'product_name', label: 'Produto', types: [types.name()], placeholder: '', title: 'Nome/taxonomia do produto' },
		{ index: 15, field: 'manufacturer_nick', label: 'Fabricante', types: [types.name()], placeholder: '', title: 'Apelido do fabricante (produto)' },
		{ index: 16, field: 'ncm', label: 'NCM', types: [types.name()], placeholder: '', title: 'NCM do produto' },
		{ index: 17, field: 'gtin', label: 'EAN', types: [types.name()], placeholder: '', title: 'GTIN/EAN do produto' },
		{ index: 18, field: 'codigo_cliente', label: 'Cód. cliente', types: [types.name()], placeholder: '', title: 'Código do cliente no produto' },
		{ index: 19, field: 'packaging_code', label: 'Embalagem', types: [types.name()], placeholder: '', title: 'Código da embalagem padrão do produto' },
		{ index: 20, field: 'packaging_factor', label: 'Fator Emb.', types: [types.number(0, 2)], placeholder: '', title: 'Fator da embalagem padrão' },
		{ index: 21, field: 'packaging_qr_code', label: 'QR Emb.', types: [types.name()], placeholder: '', title: 'QR code da embalagem padrão' },
		{ index: 22, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa do lote', store: 'programs', labels: ['name'] },
		{ index: 24, field: 'lot_number', label: 'Lote fab.', types: [types.name()], placeholder: '', title: 'Número do lote fabricante' },
		{ index: 26, field: 'expiration_date', label: 'Validade', types: [types.date()], placeholder: '', title: 'Data de validade do lote' },
		{ index: 25, field: 'manufacture_date', label: 'Fabricação', types: [types.date()], placeholder: '', title: 'Data de fabricação do lote' },
		{ index: 28, field: 'input_created_on', label: 'Data Entrada', types: [types.date()], placeholder: '', title: 'Data da entrada referente ao lote' },
		{ index: 27, field: 'input_id', label: 'Entrada Sial', types: [types.number(0, 2)], placeholder: '', title: 'Número da entrada SIAL referente ao lote' },
		{ index: 29, field: 'invoice_number', label: 'Nota Entrada', types: [types.name()], placeholder: '', title: 'Número da nota de entrada' },
		{ index: 30, field: 'supplier_name', label: 'Fornecedor', types: [types.name()], placeholder: '', title: 'Fornecedor da entrada' },
		{ index: 3, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém do estoque', store: 'warehouses', labels: ['name'] },
		{ index: 4, field: 'location_id', label: 'Posição', types: [types.datalist()], placeholder: '', title: 'Posição no armazém', store: 'locations', labels: ['address'], separator: '-' },
		{ index: 5, field: 'lot_company_id', label: 'Titular', types: [types.datalist()], placeholder: '', title: 'Titular do lote', store: 'companies', labels: ['nick'] },
		{ index: 6, field: 'lot_id', label: 'Lote', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do lote' },
		{ index: 7, field: 'quantity_available', label: 'Disponível', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade disponível' },
		{ index: 8, field: 'quantity_reserved', label: 'Reservado', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade reservada' },
		{ index: 9, field: 'quantity_unsettled', label: 'Bloqueado', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade sob análise' },
		{ index: 10, field: 'status', label: 'Status', types: [types.opt_stock_status(), types.datalist()], placeholder: '', title: 'Status do estoque' },
	];

	let operations = [
		{ hash: '#move-stock', label: 'Mover&nbsp;estoque' },
	];

	let reports = [
		{ hash: '#print-by-sku',		label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;SKU' },
		{ hash: '#print-by-lot',		label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;Lote' },
		{ hash: '#print-by-location',	label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;Posição' },
		{ hash: '#export-csv',			label: 'Exportar&nbsp;CSV&nbsp;(Relatório&nbsp;Completo)' },
		{ hash: '#lot-labels',			label: 'Etiquetas&nbsp;de&nbsp;Lotes' },
	];

	router.set('#move-stock', 			stocks_crud.moveStock.bind(stocks_crud));
	router.set('#print-by-sku',			stocks_crud.printBySKU.bind(stocks_crud));
	router.set('#print-by-lot',			stocks_crud.printByLot.bind(stocks_crud));
	router.set('#print-by-location',	stocks_crud.printByLocation.bind(stocks_crud));
	router.set('#export-csv',			stocks_crud.exportCsv.bind(stocks_crud));
	router.set('#lot-labels',			stocks_crud.printLotLabels.bind(stocks_crud));

	stocks_crud.run(
		'stocks',
		'Estoques',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);

};

export default stocks;

